import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { Clerk } from "@clerk/clerk-js";
import { ReferenceViewer } from "./ReferenceViewer";
import { Loader2, FileQuestion, RefreshCw, LogOut, AlertTriangle } from "lucide-react";

// Clear all Rima auth state from localStorage and Clerk cookies
function clearAuthCache() {
  console.log("[Rima Excel] Clearing all auth cache");
  localStorage.removeItem("rima_excel_auth");
  // Clear any Clerk-related localStorage keys
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("clerk") || key.startsWith("__clerk")) {
      localStorage.removeItem(key);
    }
  });
  // Clear Clerk cookies to prevent "name too long" errors after account switch
  try {
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name.startsWith("__clerk") || name.startsWith("__session") || name.startsWith("__client")) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });
  } catch (e) {
    // Cookie access may fail in some WebView contexts
  }
}

// Global error handler — catches unhandled errors/promises that escape React's error boundary
// (e.g. Clerk throwing during load before React renders). Prevents the WebView2 yellow error bar.
window.addEventListener("unhandledrejection", (event) => {
  console.error("[Rima Excel] Unhandled promise rejection:", event.reason);
  event.preventDefault(); // Suppress WebView2 yellow error bar
});

window.addEventListener("error", (event) => {
  console.error("[Rima Excel] Unhandled error:", event.error);
  event.preventDefault(); // Suppress WebView2 yellow error bar
});

// Error boundary — catches runtime crashes (stale Clerk state, account switch, etc.)
// and resets to a clean login screen
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[Rima Excel] App crashed:", error, info.componentStack);
  }

  handleReset = () => {
    clearAuthCache();
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col h-screen bg-white font-sans items-center justify-center px-6">
          <div className="flex flex-col items-center text-center max-w-xs">
            <div className="w-14 h-14 rounded-full bg-yellow-50 flex items-center justify-center mb-5">
              <AlertTriangle className="w-7 h-7 text-yellow-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-sm text-gray-500 leading-relaxed mb-1">
              This can happen when switching Microsoft accounts.
            </p>
            <p className="text-xs text-gray-400 mb-6">
              Click below to clear cached data and sign in again.
            </p>
            <button
              onClick={this.handleReset}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Reset &amp; Reload
            </button>
          </div>
          <footer className="absolute bottom-4">
            <span className="text-[10px] text-gray-300 font-mono">Rima v1.0</span>
          </footer>
        </div>
      );
    }
    return this.props.children;
  }
}

// Rima Logo Component
const RimaLogo = ({ size = 36 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.6173 0.158513C17.8623 0.0570186 18.1377 0.0570187 18.3827 0.158513L30.3452 5.11356C30.5903 5.21506 30.7849 5.40973 30.8864 5.65476L35.8415 17.6173C35.943 17.8623 35.943 18.1377 35.8415 18.3827L30.8864 30.3452C30.7849 30.5903 30.5903 30.7849 30.3452 30.8864L18.3827 35.8415C18.1377 35.943 17.8623 35.943 17.6173 35.8415L5.65476 30.8864C5.40973 30.7849 5.21506 30.5903 5.11356 30.3452L0.158513 18.3827C0.0570186 18.1377 0.0570187 17.8623 0.158513 17.6173L5.11356 5.65476C5.21506 5.40973 5.40973 5.21506 5.65476 5.11356L17.6173 0.158513Z"
      fill="url(#rima_gradient)"
    />
    <path
      d="M17.8086 0.620117C17.9311 0.569405 18.0689 0.569405 18.1914 0.620117L30.1543 5.5752C30.2766 5.62594 30.3741 5.72337 30.4248 5.8457L35.3799 17.8086C35.4306 17.9311 35.4306 18.0689 35.3799 18.1914L30.4248 30.1543C30.3741 30.2766 30.2766 30.3741 30.1543 30.4248L18.1914 35.3799C18.0689 35.4306 17.9311 35.4306 17.8086 35.3799L5.8457 30.4248C5.72337 30.3741 5.62594 30.2766 5.5752 30.1543L0.620117 18.1914C0.569405 18.0689 0.569405 17.9311 0.620117 17.8086L5.5752 5.8457C5.62594 5.72338 5.72338 5.62594 5.8457 5.5752L17.8086 0.620117Z"
      stroke="rgba(225,180,250,0.5)"
      strokeOpacity="1"
    />
    <defs>
      <radialGradient
        id="rima_gradient"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(18 18) rotate(90) scale(17.4545)"
      >
        <stop offset="0%" stopColor="#E1B4FA" />
        <stop offset="100%" stopColor="#FFFFFF" />
      </radialGradient>
    </defs>
  </svg>
);

const API_BASE_URL = process.env.API_BASE_URL || "https://api-test.getrima.ai";
const CLERK_PUBLISHABLE_KEY = process.env.CLERK_PUBLISHABLE_KEY || "pk_test_Y3VycmVudC1zY29ycGlvbi04NC5jbGVyay5hY2NvdW50cy5kZXYk";

console.log("[Rima Excel] Initializing...");
console.log("[Rima Excel] API URL:", API_BASE_URL);

interface CellReference {
  cell: string;
  sheet: string;
  value: string | number | null;
}

interface RunInfo {
  runId: string;
  runName?: string;
}

interface AuthSession {
  token: string;
  userEmail: string;
  expiresAt: number;
}

const App: React.FC = () => {
  const [clerk, setClerk] = useState<Clerk | null>(null);
  const [clerkLoading, setClerkLoading] = useState(true);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  // Initialize Clerk
  useEffect(() => {
    const initClerk = async () => {
      try {
        // Strip Office query params (et=, _host_Info=) from URL before Clerk loads.
        // These can be extremely long and cause "name too long" cookie errors in Clerk.
        if (window.location.search && (window.location.search.includes("et=") || window.location.search.includes("_host_Info="))) {
          console.log("[Rima Excel] Stripping Office query params from URL");
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Clear stale Clerk cookies to prevent errors after switching Microsoft accounts
        try {
          document.cookie.split(";").forEach((c) => {
            const name = c.split("=")[0].trim();
            if (name.startsWith("__clerk") || name.startsWith("__session") || name.startsWith("__client")) {
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            }
          });
        } catch (e) {
          // Cookie access may fail
        }

        const clerkInstance = new Clerk(CLERK_PUBLISHABLE_KEY);
        await clerkInstance.load({
          // Disable routing to avoid history API issues in Office Add-ins
          routerPush: () => {},
          routerReplace: () => {},
        });
        setClerk(clerkInstance);
        console.log("[Rima Excel] Clerk loaded");

        // Clean up URL after OAuth redirect (remove __clerk_handshake params)
        if (window.location.search.includes("__clerk_handshake")) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Check if already signed in
        if (clerkInstance.user) {
          const token = await clerkInstance.session?.getToken();
          const email = clerkInstance.user.primaryEmailAddress?.emailAddress;
          if (token && email) {
            // If inside an Office dialog, send token to parent and stop
            if (IS_OFFICE_DIALOG) {
              console.log("[Rima Excel] In dialog, sending auth to parent for:", email);
              Office.context.ui.messageParent(
                JSON.stringify({ type: "auth_success", token, email })
              );
              return;
            }
            const newSession: AuthSession = {
              token,
              userEmail: email,
              expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days
            };
            setSession(newSession);
            localStorage.setItem("rima_excel_auth", JSON.stringify(newSession));
            console.log("[Rima Excel] Already signed in as:", email);
          }
        } else {
          // Clear stale Clerk session to prevent "You are signed out" banner
          // in Edge/WebView2 where cookie handling can leave partial session data
          try {
            await clerkInstance.signOut();
          } catch (e) {
            // Expected when there's no active session
          }

          // Check for cached session
          const savedSession = localStorage.getItem("rima_excel_auth");
          if (savedSession) {
            try {
              const parsed = JSON.parse(savedSession) as AuthSession;
              if (parsed.expiresAt > Date.now()) {
                setSession(parsed);
                console.log("[Rima Excel] Restored cached session for:", parsed.userEmail);
              } else {
                localStorage.removeItem("rima_excel_auth");
              }
            } catch (e) {
              localStorage.removeItem("rima_excel_auth");
            }
          }
        }
      } catch (e) {
        console.error("[Rima Excel] Failed to load Clerk:", e);
        // Clear stale auth data — this often happens after switching Microsoft accounts
        clearAuthCache();
        setSession(null);
        setAuthError("Failed to initialize authentication. This can happen after switching accounts.");
      } finally {
        setClerkLoading(false);
      }
    };

    initClerk();
  }, []);

  // Listen for Clerk user changes
  useEffect(() => {
    if (!clerk) return undefined;

    const handleUserChange = async (): Promise<void> => {
      if (clerk.user) {
        const token = await clerk.session?.getToken();
        const email = clerk.user.primaryEmailAddress?.emailAddress;
        if (token && email) {
          // If inside an Office dialog, send token to parent
          if (IS_OFFICE_DIALOG) {
            console.log("[Rima Excel] In dialog, sending auth to parent for:", email);
            Office.context.ui.messageParent(
              JSON.stringify({ type: "auth_success", token, email })
            );
            return;
          }
          const newSession: AuthSession = {
            token,
            userEmail: email,
            expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
          };
          setSession(newSession);
          localStorage.setItem("rima_excel_auth", JSON.stringify(newSession));
          console.log("[Rima Excel] Signed in as:", email);
        }
      }
    };

    const unsubscribe = clerk.addListener(handleUserChange);
    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [clerk]);

  const handleSignOut = async () => {
    try {
      if (clerk) {
        await clerk.signOut();
      }
    } catch (e) {
      console.warn("[Rima Excel] Error during sign out:", e);
    }
    setSession(null);
    clearAuthCache();
    console.log("[Rima Excel] Signed out");
  };

  // Core dialog auth logic — opens Office Dialog, returns fresh token via callback
  const openAuthDialog = useCallback((onToken?: (token: string, email: string) => void) => {
    setAuthError(null);

    Office.context.ui.displayDialogAsync(
      AUTH_DIALOG_URL,
      { height: 60, width: 30, displayInIframe: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("[Rima Excel] Failed to open auth dialog:", asyncResult.error);
          setAuthError("Failed to open sign-in. Please try again.");
          return;
        }

        const dialog = asyncResult.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
          dialog.close();
          try {
            const message = JSON.parse(arg.message);
            if (message.type === "auth_success" && message.token && message.email) {
              const newSession: AuthSession = {
                token: message.token,
                userEmail: message.email,
                expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
              };
              setSession(newSession);
              localStorage.setItem("rima_excel_auth", JSON.stringify(newSession));
              console.log("[Rima Excel] Auth via dialog, signed in as:", message.email);
              if (onToken) onToken(message.token, message.email);
            }
          } catch (e) {
            console.error("[Rima Excel] Failed to parse dialog message:", e);
            setAuthError("Sign-in failed. Please try again.");
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: any) => {
          console.log("[Rima Excel] Dialog closed or event:", arg.error);
        });
      }
    );
  }, []);

  // For LoginScreen — just open dialog
  const handleDialogAuth = useCallback(() => openAuthDialog(), [openAuthDialog]);

  // For AuthenticatedApp — refresh token silently, returns a promise with the new token
  const refreshTokenViaDialog = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      console.log("[Rima Excel] Token expired, refreshing via dialog...");
      openAuthDialog((token) => resolve(token));
      // If dialog fails or user closes it, resolve null after 60s
      setTimeout(() => resolve(null), 60000);
    });
  }, [openAuthDialog]);

  if (clerkLoading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-white">
        <RimaLogo size={40} />
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 mt-4" />
      </div>
    );
  }

  if (!session) {
    return (
      <LoginScreen
        clerk={clerk}
        error={authError}
        useDialogAuth={USE_DIALOG_AUTH}
        onDialogAuth={handleDialogAuth}
      />
    );
  }

  return <AuthenticatedApp session={session} clerk={clerk} onSignOut={handleSignOut} onRefreshToken={refreshTokenViaDialog} />;
};

// Stable redirect URL for Clerk (avoids window.location issues in Office WebView)
const CLERK_REDIRECT_URL = `${process.env.PLUGIN_URL}/taskpane.html`;

// Detect if running inside an Office dialog (e.g. after OAuth redirect landed on taskpane.html)
// In dialog context: messageParent exists but displayDialogAsync does not
const IS_OFFICE_DIALOG = (() => {
  try {
    return (
      typeof Office.context.ui.messageParent === "function" &&
      typeof Office.context.ui.displayDialogAsync !== "function"
    );
  } catch {
    return false;
  }
})();

// Use Office Dialog API for auth on all platforms (consistent UX, avoids
// WebView2/WebKit redirect issues with Clerk satellite domain handshake)
const USE_DIALOG_AUTH = (() => {
  try {
    return (
      !IS_OFFICE_DIALOG &&
      typeof Office.context.ui.displayDialogAsync === "function"
    );
  } catch {
    return false;
  }
})();
const AUTH_DIALOG_URL = `${process.env.PLUGIN_URL}/auth.html`;

// Login screen component with embedded Clerk
function LoginScreen({
  clerk,
  error,
  useDialogAuth,
  onDialogAuth,
}: {
  clerk: Clerk | null;
  error: string | null;
  useDialogAuth: boolean;
  onDialogAuth: () => void;
}) {
  const signInRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [signInTimedOut, setSignInTimedOut] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const mountClerkSignIn = useCallback(() => {
    if (!clerk || !signInRef.current || useDialogAuth) return;
    signInRef.current.innerHTML = "";
    setSignInTimedOut(false);

    // Start a 45s timeout for sign-in completion
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (!clerk.user) {
        setSignInTimedOut(true);
      }
    }, 45000);

    console.log("[Rima Excel] Mounting Clerk SignIn with redirect:", CLERK_REDIRECT_URL);
    clerk.mountSignIn(signInRef.current, {
      redirectUrl: CLERK_REDIRECT_URL,
      forceRedirectUrl: CLERK_REDIRECT_URL,
      fallbackRedirectUrl: CLERK_REDIRECT_URL,
      appearance: {
        elements: {
          rootBox: {
            width: "100%",
            display: "flex",
            justifyContent: "center",
          },
          card: {
            boxShadow: "none",
            border: "none",
            backgroundColor: "transparent",
            width: "100%",
            maxWidth: "400px",
          },
          headerTitle: { display: "none" },
          headerSubtitle: { display: "none" },
          socialButtonsBlockButton: {
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
          },
          formFieldInput: {
            borderRadius: "8px",
          },
          formButtonPrimary: {
            backgroundColor: "#111827",
            borderRadius: "8px",
          },
          footerAction: { display: "none" },
        },
      },
    });
    setMounted(true);
  }, [clerk, useDialogAuth]);

  useEffect(() => {
    if (!mounted && !useDialogAuth) {
      mountClerkSignIn();
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (clerk && mounted && signInRef.current) {
        clerk.unmountSignIn(signInRef.current);
      }
    };
  }, [clerk, mounted, mountClerkSignIn]);

  const handleRetry = () => {
    setMounted(false);
    setSignInTimedOut(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Re-mount on next render
    setTimeout(() => mountClerkSignIn(), 100);
  };

  return (
    <div className="flex flex-col h-screen bg-white font-sans">
      {/* Main Content - Centered */}
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="w-full flex flex-col items-center">
          {/* Logo and Title */}
          <div className="flex flex-col items-center mb-6 text-center px-2">
            <RimaLogo size={48} />
            <h1 className="mt-4 text-xl font-semibold text-gray-900">Welcome to Rima</h1>
            <p className="mt-2 text-sm text-gray-600">Understand where your results come from.</p>
            <p className="mt-1 text-xs text-gray-500 px-2">Sign in to trace source data, transformations, and audit-ready outputs.</p>
          </div>

          {/* Error / Timeout Message */}
          {(error || (!useDialogAuth && signInTimedOut)) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg mx-4">
              <p className="text-sm text-red-600 text-center">
                {error || "Sign-in didn't complete. Please try again."}
              </p>
              <button
                onClick={useDialogAuth ? onDialogAuth : handleRetry}
                className="mt-2 w-full text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg py-2 px-4 transition-colors"
              >
                Try again
              </button>
            </div>
          )}

          {/* Sign In */}
          {useDialogAuth ? (
            <div className="w-full flex flex-col items-center py-4 gap-3">
              <button
                onClick={onDialogAuth}
                className="w-48 py-3 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Sign in
              </button>
            </div>
          ) : (
            <div ref={signInRef} className="w-full flex justify-center">
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 text-center">
        <span className="text-[10px] text-gray-400 font-mono">Rima v1.0</span>
      </footer>
    </div>
  );
}

// Main authenticated app
function AuthenticatedApp({ session, clerk, onSignOut, onRefreshToken }: { session: AuthSession; clerk: Clerk | null; onSignOut: () => void; onRefreshToken: () => Promise<string | null> }) {
  const [selectedCell, setSelectedCell] = useState<CellReference | null>(null);
  const [sourceData, setSourceData] = useState<any>(null);
  const [loadingSource, setLoadingSource] = useState(false);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [runInfo, setRunInfo] = useState<RunInfo | null>(null);
  const [loadingRunInfo, setLoadingRunInfo] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Read hidden sheet for run ID on mount
  useEffect(() => {
    readHiddenSheet();
  }, []);

  // Setup cell selection listener
  useEffect(() => {
    const setupCellSelectionHandler = async () => {
      try {
        await Office.onReady();

        Office.context.document.addHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          handleSelectionChanged,
          (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              console.error("Failed to add selection handler:", result.error);
            }
          }
        );

        handleSelectionChanged();
      } catch (e) {
        console.error("Failed to setup Office handlers:", e);
      }
    };

    setupCellSelectionHandler();

    return () => {
      try {
        Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
          handler: handleSelectionChanged,
        });
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, []);

  // Fetch summary data when run is loaded
  useEffect(() => {
    if (runInfo) {
      fetchSummaryData();
    }
  }, [runInfo?.runId]);

  // Fetch source data when cell changes
  useEffect(() => {
    if (runInfo && selectedCell) {
      fetchSourceData(selectedCell);
    }
  }, [runInfo, selectedCell?.cell, selectedCell?.sheet]);

  // Read the hidden sheet to get run ID
  const readHiddenSheet = async () => {
    setLoadingRunInfo(true);
    setError(null);

    try {
      await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        sheets.load("items/name,items/visibility");
        await context.sync();

        // Log all sheets for debugging
        console.log("[Rima Excel] All sheets in workbook:");
        for (const sheet of sheets.items) {
          console.log(`  - "${sheet.name}" (visibility: ${sheet.visibility})`);
        }

        // Look for hidden sheet "_recon_meta"
        let foundSheet: Excel.Worksheet | null = null;

        for (const sheet of sheets.items) {
          const sheetNameLower = sheet.name.toLowerCase();
          if (sheetNameLower === "_recon_meta") {
            console.log("[Rima Excel] Found _recon_meta sheet!");
            foundSheet = sheet;
            break;
          }
        }

        // Fallback: try any hidden sheet
        if (!foundSheet) {
          console.log("[Rima Excel] _recon_meta not found, looking for any hidden sheet...");
          for (const sheet of sheets.items) {
            if (
              sheet.visibility === Excel.SheetVisibility.hidden ||
              sheet.visibility === Excel.SheetVisibility.veryHidden
            ) {
              console.log(`[Rima Excel] Found hidden sheet: "${sheet.name}"`);
              foundSheet = sheet;
              break;
            }
          }
        }

        if (foundSheet) {
          const range = foundSheet.getRange("A1:B10");
          range.load("values");
          await context.sync();

          const values = range.values;
          console.log("[Rima Excel] Sheet values:", JSON.stringify(values));

          let runId: string | null = null;
          let runName: string | null = null;

          for (const row of values) {
            const key = String(row[0]).toLowerCase();
            const value = String(row[1]);
            if (key === "run_id" || key === "runid") {
              runId = value;
            } else if (key === "run_name" || key === "runname") {
              runName = value;
            }
          }

          if (!runId && values[0][0]) {
            runId = String(values[0][0]);
          }

          if (runId && runId !== "undefined" && runId !== "null") {
            console.log("[Rima Excel] Found run ID:", runId);
            setRunInfo({ runId, runName: runName || undefined });
          } else {
            console.log("[Rima Excel] No run_id found in sheet values");
            setError("This file does not contain Rima metadata. Please open a file from the Runs results page in the application.");
          }
        } else {
          console.log("[Rima Excel] No hidden sheet found");
          setError("This file does not contain Rima metadata. Please open a file from the Runs results page in the application.");
        }
      });
    } catch (e) {
      console.error("[Rima Excel] Failed to read hidden sheet:", e);
      setError("Failed to read file metadata. Please try again.");
    } finally {
      setLoadingRunInfo(false);
    }
  };

  const handleSelectionChanged = useCallback(async () => {
    try {
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        range.load(["address", "values", "worksheet"]);
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load("name");

        await context.sync();

        const address = range.address;
        const cellRef = address.includes("!") ? address.split("!")[1] : address;
        const sheetName = sheet.name;
        const value = range.values[0]?.[0] ?? null;

        setSelectedCell({
          cell: cellRef,
          sheet: sheetName,
          value: value,
        });
      });
    } catch (e) {
      console.error("Failed to get selected cell:", e);
    }
  }, []);

  // Get a fresh token — tries Clerk session first, then falls back to stored token
  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      const freshToken = await clerk?.session?.getToken();
      if (freshToken) {
        console.log("[Rima Excel] Got fresh token from Clerk");
        return freshToken;
      }
    } catch (e) {
      console.warn("[Rima Excel] Failed to get fresh Clerk token:", e);
    }
    console.log("[Rima Excel] Using stored session token");
    return session.token;
  }, [clerk, session.token]);

  // Try Clerk skipCache refresh, then fall back to dialog-based refresh
  const refreshToken = useCallback(async (): Promise<string | null> => {
    // First try Clerk's built-in refresh
    try {
      const freshToken = await clerk?.session?.getToken({ skipCache: true });
      if (freshToken) {
        console.log("[Rima Excel] Refreshed token via Clerk");
        return freshToken;
      }
    } catch (e) {
      console.warn("[Rima Excel] Clerk refresh failed:", e);
    }
    // Fall back to re-opening auth dialog
    console.log("[Rima Excel] Falling back to dialog-based token refresh");
    return onRefreshToken();
  }, [clerk, onRefreshToken]);

  // Helper: make an authenticated API call with automatic token refresh on 401
  const fetchWithAuth = useCallback(async (url: string, body: object): Promise<Response | null> => {
    let token = await getToken();
    if (!token) {
      onSignOut();
      return null;
    }

    let response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });

    if (response.status === 401) {
      console.log("[Rima Excel] 401 — refreshing token...");
      const newToken = await refreshToken();
      if (newToken) {
        response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${newToken}` },
          body: JSON.stringify(body),
        });
      }
    }

    // Still 401 after refresh — sign out
    if (response.status === 401) {
      console.log("[Rima Excel] Token refresh failed, signing out");
      onSignOut();
      return null;
    }

    // Server error — sign out and clear cache
    if (response.status >= 500) {
      console.error(`[Rima Excel] Server error ${response.status} from ${url}, signing out`);
      onSignOut();
      return null;
    }

    return response;
  }, [getToken, refreshToken, onSignOut]);

  const fetchSummaryData = async () => {
    if (!runInfo) return;

    setLoadingSummary(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/recon/source/summary`,
        { run_id: runInfo.runId }
      );
      if (!response) return;
      if (!response.ok) {
        console.error("[Rima Excel] Summary API error:", response.status);
        if (response.status >= 500) {
          setSummaryData({ serverError: true });
        }
        return;
      }

      const data = await response.json();
      console.log("[Rima Excel] Summary API Response data:", JSON.stringify(data, null, 2));
      setSummaryData(data);
    } catch (e) {
      console.error("[Rima Excel] Failed to fetch summary data:", e);
    } finally {
      setLoadingSummary(false);
    }
  };

  const fetchSourceData = async (cellRef: CellReference) => {
    if (!runInfo) return;

    setLoadingSource(true);
    setSourceData(null);

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/recon/source`,
        { run_id: runInfo.runId, cell_id: cellRef.cell }
      );

      if (!response) return;

      if (!response.ok) {
        if (response.status === 404) {
          setSourceData({ notFound: true });
        } else if (response.status >= 500) {
          console.error("[Rima Excel] Source API server error:", response.status);
          setSourceData({ error: true, serverError: true });
        } else {
          throw new Error(`API error: ${response.status}`);
        }
        return;
      }

      // Handle JSON response with images array
      const data = await response.json();
      console.log("[Rima Excel] API Response data:", JSON.stringify(data, null, 2));

      // Check if API returned success but no source found
      if (data.status === 404 || data.found === false || data.success === false) {
        console.log("[Rima Excel] API returned not found in response body");
        setSourceData({ notFound: true });
        return;
      }

      // Check if images array is empty
      if (!data.images || data.images.length === 0) {
        console.log("[Rima Excel] No images in response");
        setSourceData({ notFound: true });
        return;
      }

      // New format: { cell_id, images: [{ file_name, page_number, image_url }] }
      setSourceData({
        cell_id: data.cell_id,
        images: data.images,
      });
    } catch (e) {
      console.error("Failed to fetch source data:", e);
      setSourceData({ error: true });
    } finally {
      setLoadingSource(false);
    }
  };

  if (loadingRunInfo) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 mb-3" />
        <span className="text-sm text-gray-500">Reading file...</span>
      </div>
    );
  }

  if (error || !runInfo) {
    return (
      <div className="flex flex-col h-screen bg-white font-sans">
        {/* Minimal Header */}
        <header className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h1 className="text-base font-semibold text-gray-900">Rima</h1>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 truncate max-w-[120px]">{session.userEmail}</span>
              <button
                onClick={onSignOut}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Sign out"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </header>

        {/* Full height centered content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="flex flex-col items-center text-center max-w-xs">
            {/* Icon */}
            <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mb-5">
              <FileQuestion className="w-7 h-7 text-gray-400" />
            </div>

            {/* Title */}
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              No Rima file detected
            </h2>

            {/* Description */}
            <p className="text-sm text-gray-500 leading-relaxed">
              {error || "Open an Excel file exported from Rima Work Manager to view source references."}
            </p>

            {/* Retry button */}
            <button
              onClick={readHiddenSheet}
              className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className="px-4 py-2 border-t border-gray-100 text-center">
          <span className="text-[10px] text-gray-400 font-mono">Rima v1.0</span>
        </footer>
      </div>
    );
  }

  return (
    <ReferenceViewer
      runInfo={runInfo}
      selectedCell={selectedCell}
      sourceData={sourceData}
      loading={loadingSource}
      loadingData={false}
      dataLoaded={true}
      onRefresh={() => selectedCell && fetchSourceData(selectedCell)}
      onSignOut={onSignOut}
      userEmail={session.userEmail}
      summaryData={summaryData}
      loadingSummary={loadingSummary}
    />
  );
}

function AppWithErrorBoundary() {
  return (
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  );
}

export default AppWithErrorBoundary;
